import { createReducer, createActions } from 'reduxsauce'
import Immutable from 'seamless-immutable'

/* ------------- Types and Action Creators ------------- */
// Isolated store for the Ad Library feature (NOT registered in the global
// src/redux store). It runs entirely on API data — clients/brands and now
// contents (ads) and content-tags (folders). The reducer only holds state;
// every side effect lives in AdLibrarySagas.
//
// `scope` carries who we list for: { id, isCompany } — advertisers list by
// company, individual users list by user (mirrors ContentsLibraryComponent).
const { Types, Creators } = createActions({
  toggleSelect: ['id'],
  clearSelection: [],
  selectAll: ['ids'],

  // Folders (content-tags) — real API: GET /companies|users/{id}/contents-tags
  getTagsRequest: ['scope'],
  getTagsSuccess: ['folders'],
  getTagsFailure: ['errors'],
  setSelectedTag: ['tag'],

  // Ads (contents) — real API: GET /companies|users/{id}/contents (datatable).
  // `search` filters server-side (the endpoint's `search` param). `types` is the
  // server-side type filter (array, e.g. ['image']); empty/absent => both types.
  // NOTE: it's `types`, not `type` — `type` is redux's reserved action key, so a
  // payload field named `type` would clobber the action type and break dispatch.
  // `page` is the 1-based ads page (server-side; 24 per page). Folders are not paginated.
  // `sort` is the server-side ordering ('name' | 'oldest' | 'newest'); absent/invalid => backend default.
  getContentsRequest: ['scope', 'tag', 'search', 'types', 'page', 'sort'],
  getContentsSuccess: ['ads', 'total'],
  getContentsFailure: ['errors'],

  // Ad mutations with a real endpoint (PUT /contents/{id}, POST /contents)
  renameAdRequest: ['id', 'name'],
  renameAdSuccess: [],
  renameAdFailure: ['errors'],
  deleteAdsRequest: ['ids'],
  deleteAdsSuccess: [],
  deleteAdsFailure: ['errors'],
  uploadAdsRequest: ['files', 'meta'],
  uploadAdsSuccess: [],
  uploadAdsFailure: ['errors'],
  // Per-file upload result, appended as each file resolves so the UI can show an
  // individual toast per file. `result`: { index, name, ok }.
  uploadAdProgress: ['result'],

  // Create folder — real API: POST /contents-tags. `parentId` (optional) nests
  // it as a subfolder; omit/null => root folder. Saga refetches the tree on success.
  createFolderRequest: ['name', 'parentId'],
  createFolderSuccess: [],
  createFolderFailure: ['errors'],

  // Rename folder — real API: PUT /contents-tags/{id}. Saga refetches the tree.
  // `parentId` MUST be sent (the folder's current parent) — omitting it makes the
  // backend turn the folder into a root folder.
  renameFolderRequest: ['id', 'name', 'parentId'],
  renameFolderSuccess: [],
  renameFolderFailure: ['errors'],

  // Delete folders (logical, bulk) — PUT /contents-tags/archive { ids }. Mirrors
  // the ad bulk delete (deleteAdsRequest); works for one or many folders. Saga
  // refetches the folder tree on success.
  deleteFoldersRequest: ['ids'],
  deleteFoldersSuccess: [],
  deleteFoldersFailure: ['errors'],

  // Clients — real API (api.getClients). Brands come embedded in each client
  // (client.brands), so there is no separate brands fetch. `search` filters
  // server-side (the endpoint's `search` param).
  getClientsRequest: ['userId', 'search'],
  getClientsSuccess: ['clients'],
  getClientsFailure: ['errors'],
})

export const AdLibraryTypes = Types
export default Creators

/* ------------- Initial State ------------- */

export const INITIAL_STATE = Immutable({
  ads: [],
  adsTotal: 0,
  folders: [],
  selectedIds: [],
  selectedTag: null,
  // Remembered so mutation sagas can refetch the current listing.
  contentScope: null,
  contentSearch: '',
  contentType: [],
  contentPage: 1,
  contentSort: null,
  clients: [],
  loadingClients: false,
  loadingAds: false,
  loadingFolders: false,
  uploading: false,
  // Per-file upload results for the current upload ({ index, name, ok }), reset
  // when a new upload starts. Drives the individual per-file toasts.
  uploadProgress: [],
  folderSubmitting: false,
  folderError: null,
  errors: [],
})

/* ------------- Reducers ------------- */

export const toggleSelect = (state, { id }) => {
  const selected = state.selectedIds
  const next = selected.includes(id)
    ? selected.filter((x) => x !== id)
    : [...selected, id]
  return state.merge({ selectedIds: next })
}

export const clearSelection = (state) => state.merge({ selectedIds: [] })

export const selectAll = (state, { ids }) => state.merge({ selectedIds: ids })

/* ----- Folders (content-tags) ----- */

export const getTagsRequest = (state) =>
  state.merge({ loadingFolders: true, errors: [] })

export const getTagsSuccess = (state, { folders }) =>
  state.merge({ folders, loadingFolders: false })

export const getTagsFailure = (state, { errors }) =>
  state.merge({ loadingFolders: false, errors })

export const setSelectedTag = (state, { tag }) => state.merge({ selectedTag: tag })

/* ----- Ads (contents) ----- */

export const getContentsRequest = (state, { scope, search, types, page, sort }) =>
  state.merge({
    loadingAds: true,
    errors: [],
    contentScope: scope || state.contentScope,
    contentSearch: search || '',
    contentType: types || [],
    contentPage: page || 1,
    contentSort: sort || null,
  })

export const getContentsSuccess = (state, { ads, total }) =>
  state.merge({ ads, adsTotal: total || 0, loadingAds: false })

export const getContentsFailure = (state, { errors }) =>
  state.merge({ loadingAds: false, errors })

/* ----- Ad mutations (real endpoints; saga refetches on success) ----- */

export const renameAdRequest = (state) => state.merge({ errors: [] })
export const renameAdSuccess = (state) => state
export const renameAdFailure = (state, { errors }) => state.merge({ errors })

export const deleteAdsRequest = (state) => state.merge({ errors: [] })
export const deleteAdsSuccess = (state) => state.merge({ selectedIds: [] })
export const deleteAdsFailure = (state, { errors }) => state.merge({ errors })

export const uploadAdsRequest = (state) =>
  state.merge({ uploading: true, errors: [], uploadProgress: [] })
export const uploadAdsSuccess = (state) => state.merge({ uploading: false })
export const uploadAdsFailure = (state, { errors }) => state.merge({ uploading: false, errors })
export const uploadAdProgress = (state, { result }) =>
  state.merge({ uploadProgress: state.uploadProgress.concat([result]) })

/* ----- Create folder (real endpoint; saga refetches the tree on success) ----- */

export const createFolderRequest = (state) =>
  state.merge({ errors: [], folderSubmitting: true, folderError: null })
export const createFolderSuccess = (state) =>
  state.merge({ folderSubmitting: false, folderError: null })
export const createFolderFailure = (state, { errors }) =>
  state.merge({ errors, folderSubmitting: false, folderError: (errors && errors[0]) || null })

export const renameFolderRequest = (state) =>
  state.merge({ errors: [], folderSubmitting: true, folderError: null })
export const renameFolderSuccess = (state) =>
  state.merge({ folderSubmitting: false, folderError: null })
export const renameFolderFailure = (state, { errors }) =>
  state.merge({ errors, folderSubmitting: false, folderError: (errors && errors[0]) || null })

/* ----- Delete folders (bulk archive; saga refetches the tree on success) ----- */

export const deleteFoldersRequest = (state) => state.merge({ errors: [] })
export const deleteFoldersSuccess = (state) => state
export const deleteFoldersFailure = (state, { errors }) => state.merge({ errors })

/* ----- Clients (brands come embedded in client.brands) ----- */

export const getClientsRequest = (state) =>
  state.merge({ loadingClients: true, errors: [] })

export const getClientsSuccess = (state, { clients }) =>
  state.merge({ clients, loadingClients: false })

export const getClientsFailure = (state, { errors }) =>
  state.merge({ loadingClients: false, errors })

/* ------------- Hookup Reducers To Types ------------- */

export const reducer = createReducer(INITIAL_STATE, {
  [Types.TOGGLE_SELECT]: toggleSelect,
  [Types.CLEAR_SELECTION]: clearSelection,
  [Types.SELECT_ALL]: selectAll,

  [Types.GET_TAGS_REQUEST]: getTagsRequest,
  [Types.GET_TAGS_SUCCESS]: getTagsSuccess,
  [Types.GET_TAGS_FAILURE]: getTagsFailure,
  [Types.SET_SELECTED_TAG]: setSelectedTag,

  [Types.GET_CONTENTS_REQUEST]: getContentsRequest,
  [Types.GET_CONTENTS_SUCCESS]: getContentsSuccess,
  [Types.GET_CONTENTS_FAILURE]: getContentsFailure,

  [Types.RENAME_AD_REQUEST]: renameAdRequest,
  [Types.RENAME_AD_SUCCESS]: renameAdSuccess,
  [Types.RENAME_AD_FAILURE]: renameAdFailure,
  [Types.DELETE_ADS_REQUEST]: deleteAdsRequest,
  [Types.DELETE_ADS_SUCCESS]: deleteAdsSuccess,
  [Types.DELETE_ADS_FAILURE]: deleteAdsFailure,
  [Types.UPLOAD_ADS_REQUEST]: uploadAdsRequest,
  [Types.UPLOAD_ADS_SUCCESS]: uploadAdsSuccess,
  [Types.UPLOAD_ADS_FAILURE]: uploadAdsFailure,
  [Types.UPLOAD_AD_PROGRESS]: uploadAdProgress,

  [Types.CREATE_FOLDER_REQUEST]: createFolderRequest,
  [Types.CREATE_FOLDER_SUCCESS]: createFolderSuccess,
  [Types.CREATE_FOLDER_FAILURE]: createFolderFailure,
  [Types.RENAME_FOLDER_REQUEST]: renameFolderRequest,
  [Types.RENAME_FOLDER_SUCCESS]: renameFolderSuccess,
  [Types.RENAME_FOLDER_FAILURE]: renameFolderFailure,
  [Types.DELETE_FOLDERS_REQUEST]: deleteFoldersRequest,
  [Types.DELETE_FOLDERS_SUCCESS]: deleteFoldersSuccess,
  [Types.DELETE_FOLDERS_FAILURE]: deleteFoldersFailure,

  [Types.GET_CLIENTS_REQUEST]: getClientsRequest,
  [Types.GET_CLIENTS_SUCCESS]: getClientsSuccess,
  [Types.GET_CLIENTS_FAILURE]: getClientsFailure,
})
